// 玩家模式股市页的「招股」页签:在认购的招股(每家一张卡,能认购)、我认购的、过往招股(能排序)、我的公司发起招股。
// 数都从引擎来(engine/web/src/ipo.rs 的 meipos),这里只摆出来、把玩家的决定变成指令:
// 250 认购(招股编号、元);252 发起第一步(公司、要多少元)、253 发起(最少几成 | 期限 << 8 | 用途 << 16、每股多少分)。
import { dateOf, esc, money, spark } from './sbchart.ts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type J = any;

/** 招股页签自己的状态:过往招股按哪一列排、正着倒着;发起招股选的是哪家公司。 */
export interface IpoView { sort: string; dir: number; firm: number | null }
export const ipoView = (): IpoView => ({ sort: 'closed', dir: -1, firm: null });

const yuan = (v: number) => (Math.abs(v) < 1e4 ? `${Math.round(v)} 元` : money(v));
const px = (v: number) => v >= 100 ? v.toFixed(0) : v.toFixed(2);
const pct = (v: number, d = 0) => `${(v * 100).toFixed(d)}%`;
const signed = (v: number | null | undefined) => v == null ? '—' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(0)}%`;
const updown = (v: number | null | undefined) => v == null ? '' : v > 0 ? ' class="up"' : v < 0 ? ' class="down"' : '';
const who = (w: J) => w?.kind === 'person' ? `<a data-go="person:${w.id}">${esc(w.name)}</a>` : w?.kind === 'firm' ? `<a data-go="firm:${w.id}">${esc(w.name)}</a>` : esc(w?.name ?? '');

/** 认购 X 元大约配到几股、占发完以后的几成(后面还有人认购、超出要的按比例配,所以是「约」)。 */
export const ipoQuote = (x: number, price: number, want: number, pot: number, shares: number) => {
  const total = pot + x;
  const alloc = total <= want ? x : x * want / total;
  const got = alloc / Math.max(price, 1e-9);
  const issued = Math.min(want, total) / Math.max(price, 1e-9);
  return { got, share: got / Math.max(shares + issued, 1e-9) };
};
const nTxt = (n: number) => n < 10 ? n.toFixed(2) : n.toFixed(0);
/** 认购按钮上的字。 */
export const ipoBuyLabel = (x: number, price: number, want: number, pot: number, shares: number) => {
  if (!(x > 0)) return '认购';
  const q = ipoQuote(x, price, want, pot, shares);
  return `认购 ${yuan(x)} · 配到约 ${nTxt(q.got)} 股,占 ${pct(q.share, q.share < 0.01 ? 2 : 1)}`;
};
/** 发起招股按钮上的字:募满以后自己剩几成。 */
export const ipoLaunchLabel = (want: number, price: number, shares: number, mine: number) => {
  if (!(want > 0) || !(price > 0)) return '发起招股';
  const left = mine / Math.max(shares + want / price, 1e-9);
  return `发起招股 · 募满你剩 ${pct(left, 1)}`;
};

/** 一张招股卡。 */
const card = (x: J, cash: number, busy: boolean) => {
  const fill = x.want > 0 ? x.pot / x.want : 0;
  const vsValue = x.value > 0 ? x.price / x.value - 1 : 0;
  const vsBook = x.book > 0 ? x.price / x.book - 1 : null;
  const tag = (v: number | null, t: string) => v == null ? '' : `${t}${v < 0 ? `折价 ${pct(-v)}` : v > 0 ? `溢价 ${pct(v)}` : '平价'}`;
  const m = x.months as number[][];
  const trend = m.length > 1 ? `<div class="sb-ipo-trend"><span>营收 ${spark(m.map((r) => r[0]), 'var(--s2)', 80, 18)}</span><span>利润 ${spark(m.map((r) => r[1]), 'var(--s5)', 80, 18)}</span><small>近 ${m.length} 个月</small></div>` : '<div class="sb-ipo-trend"><small>开张不到两个月,没有往绩</small></div>';
  const orders = (x.orders as J[]).length ? `<p class="sb-ipo-orders">认购的:${(x.orders as J[]).map((o) => `${who(o.who)} ${yuan(o.amt)}`).join('、')}${x.count > (x.orders as J[]).length ? ` 等 ${x.count} 笔` : ''}</p>` : '<p class="sb-ipo-orders">还没有人认购</p>';
  const dis = busy ? ' disabled' : '';
  // data-amt:页面刷新时不把填了还没点的数刷掉(见 sandbox.ts 的 morph)
  const box = x.isMine && x.byPlayer ? '' : `<div class="sb-amt"><input type="number" min="1" step="1" inputmode="numeric" placeholder="金额(元)" data-amt="ipo:${x.id}" data-ipo-amt="${x.id}" data-price="${x.price}" data-want="${x.want}" data-pot="${x.pot}" data-shares="${x.shares}">
    <button class="sb-sd-btn primary" data-ipo-go="buy:${x.id}"${dis}>认购</button>${x.mine > 0 ? `<small>已认购 ${yuan(x.mine)},钱冻到截止</small>` : `<small>手上能用 ${yuan(cash)}</small>`}</div>`;
  return `<div class="sb-ipo">
    <div class="sb-ipo-h"><b><a data-stk="firm:${x.firm}">${esc(x.name)}</a></b><span>${esc(x.line)} · ${esc(x.district)} · ${esc(x.kind)}${x.byPlayer ? ' · 老板自己发起' : ''}</span></div>
    <p class="sb-ipo-why">${esc(x.why)}</p>
    <div class="sb-ipo-bar"><i style="width:${Math.min(100, fill * 100).toFixed(1)}%"></i>${x.min > 0 && x.want > 0 ? `<em style="left:${Math.min(100, x.min / x.want * 100).toFixed(1)}%" title="最少要的"></em>` : ''}</div>
    <div class="sb-ipo-nums"><span>要 <b>${yuan(x.want)}</b>,最少 ${yuan(x.min)}</span><span>认购了 ${pct(fill)}${fill > 1 ? ',超出的按比例配' : ''}</span><span>还剩 ${x.left} 天</span></div>
    <div class="sb-ipo-nums"><span>每股 <b>${px(x.price)}</b> 元</span><span>${tag(vsValue, '比公司自估 ')}</span><span>${tag(vsBook, '比家底 ')}</span><span>募满老股东剩 ${pct(x.after, 1)}</span></div>
    ${trend}${orders}${box}</div>`;
};

/** 我认购的:冻着的、配到的、退回的。 */
const mine = (rows: J[]) => {
  if (!rows.length) return '<p class="sb-sd-note">还没有认购过。</p>';
  return `<table class="sb-sd-table sb-stk-table"><thead><tr><th>公司</th><th>状态</th><th>认购</th><th>配到</th><th>退回</th><th>招股价 / 现价</th></tr></thead><tbody>${rows.map((r) => `<tr data-stk="firm:${r.firm}">
    <td><b>${esc(r.name)}</b><small>${r.open ? `认购到 ${esc(dateOf(r.until))}` : ''}</small></td><td>${esc(r.state)}</td>
    <td>${r.open ? `${yuan(r.frozen)}<small>冻着</small>` : yuan(r.paid)}</td><td>${r.open ? '—' : `${nTxt(r.got)} 股`}</td><td>${r.open ? '—' : yuan(r.back)}</td>
    <td>${px(r.price)}${r.px != null ? ` / ${px(r.px)}` : ' / 公司没了'}</td></tr>`).join('')}</tbody></table>`;
};

/** 过往招股:能按列排(结束、认购倍数、募到、招股价、一个月、一年)。 */
const past = (rows: J[], v: IpoView) => {
  if (!rows.length) return '<p class="sb-sd-note">还没有结束的招股。</p>';
  const key: Record<string, (r: J) => number> = { closed: (r) => r.closed, ratio: (r) => r.ratio, raised: (r) => r.raised, price: (r) => r.price, m1: (r) => r.m1 ?? -9, y1: (r) => r.y1 ?? -9 };
  const k = key[v.sort] ?? key.closed;
  const list = rows.slice().sort((a, c) => (k(a) - k(c)) * v.dir || c.id - a.id);
  const th = (s: string, t: string) => `<th data-ipo="sort:${s}"${v.sort === s ? ' class="on"' : ''}>${t}${v.sort === s ? (v.dir > 0 ? '↑' : '↓') : ''}</th>`;
  return `<div class="sb-stk-wrap"><table class="sb-sd-table sb-stk-table"><thead><tr><th>公司</th>${th('closed', '结束')}<th>结果</th>${th('ratio', '认购倍数')}${th('raised', '募到')}${th('price', '招股价')}${th('m1', '一个月')}${th('y1', '一年')}</tr></thead><tbody>${list.map((r) => `<tr data-stk="firm:${r.firm}">
    <td><b>${esc(r.name)}</b><small>${esc(r.line)} · ${esc(r.kind)}${r.byPlayer ? ' · 老板自己发起' : ''}</small></td><td>${esc(dateOf(r.closed))}</td><td${r.ok ? '' : ' class="down"'}>${esc(r.state)}</td>
    <td>${r.ratio.toFixed(2)}</td><td>${yuan(r.raised)}<small>要 ${yuan(r.want)}</small></td><td>${px(r.price)}</td><td${updown(r.m1)}>${signed(r.m1)}</td><td${updown(r.y1)}>${signed(r.y1)}</td></tr>`).join('')}</tbody></table></div>
    <p class="sb-sd-note">一个月、一年:发完以后的股价比招股价;−100% 是公司没了。</p>`;
};

/** 我的公司发起招股。 */
const launch = (firms: J[], uses: string[], days: number, v: IpoView, busy: boolean) => {
  if (!firms.length) return '';
  const f = firms.find((x) => x.id === v.firm) ?? firms[0];
  const pick = firms.length > 1 ? `<select data-ipo-firm>${firms.map((x) => `<option value="${x.id}"${x.id === f.id ? ' selected' : ''}>${esc(x.name)}</option>`).join('')}</select>` : `<b>${esc(f.name)}</b>`;
  if (f.open != null) return `<h4>发起招股</h4><div class="sb-sd-acts">${pick}<span class="sb-sd-note">这家公司正在招股。</span></div>`;
  const dis = busy ? ' disabled' : '';
  const suggest = Math.max(0.01, f.value * 0.95);
  // 空着的按占位的数(每股价按公司自估的让五个点、认购期三十天、最少要一半);格子都带 data-amt,刷新不丢
  const box = (k: string, t: string, ph: string, step: string) => `<label>${t}<input type="number" min="0" step="${step}" placeholder="${ph}" data-amt="ipof:${k}" data-ipo-in="${k}"></label>`;
  return `<h4>发起招股<small>${esc(f.line)} · 公司自估每股 ${px(f.value)} 元 · 每股家底 ${px(f.book)} 元 · 一年挣本钱的 ${pct(f.roe)} · 你有 ${pct(f.mine / Math.max(f.shares, 1e-9), 1)}</small></h4>
    <div class="sb-ipo-form" data-ipo-form="${f.id}" data-shares="${f.shares}" data-mine="${f.mine}" data-suggest="${suggest.toFixed(2)}" data-days="${days}">${pick}
      ${box('want', '要多少(元)', '', '1')}${box('min', '最少(元)', '要的一半', '1')}${box('price', '每股(元)', suggest.toFixed(2), '0.01')}${box('days', '认购期(天)', String(days), '1')}
      <label>用途<select data-ipo-in="use">${uses.map((u, i) => `<option value="${i}"${i === 4 ? ' selected' : ''}>${esc(u)}</option>`).join('')}</select></label>
      <button class="sb-sd-btn primary" data-ipo-go="launch:${f.id}"${dis}>发起招股</button></div>`;
};

/** 「招股」页签。data 是 meipos 的输出。 */
export const ipoHTML = (data: J | null, v: IpoView, busy: boolean) => {
  if (!data) return '<p class="sb-sd-note">正在读取</p>';
  if (!data.on) return '<p class="sb-sd-note">这一版没有招股。</p>';
  const open = data.open as J[];
  const cards = open.length ? open.map((x) => card(x, data.cash, busy)).join('') : '<p class="sb-sd-note">眼下没有公司在招股。</p>';
  return `<p class="sb-sd-note">认购的钱冻到截止:够最少的发股,超出要的按比例配;不够最少的、公司倒了的全退。</p>${cards}
    <h4>我认购的</h4>${mine(data.mine as J[])}
    ${launch(data.firms as J[], data.uses as string[], data.days, v, busy)}
    <h4>过往招股<small>近三年</small></h4>${past(data.past as J[], v)}`;
};

/** 发起招股的表单填了什么(空着的按占位的数):要多少、最少、每股价、认购期、用途。 */
export const ipoForm = (form: HTMLElement) => {
  const v = (k: string) => Number(form.querySelector<HTMLInputElement>(`[data-ipo-in="${k}"]`)?.value || NaN);
  const want = Math.floor(v('want'));
  const price = Number.isFinite(v('price')) && v('price') > 0 ? v('price') : Number(form.dataset.suggest);
  const days = Number.isFinite(v('days')) && v('days') > 0 ? Math.floor(v('days')) : Number(form.dataset.days);
  const min = Number.isFinite(v('min')) && v('min') >= 0 ? Math.floor(v('min')) : Math.floor(want / 2);
  const use = Number(form.querySelector<HTMLSelectElement>('[data-ipo-in="use"]')?.value ?? 4);
  return { want, min, price, days, use };
};

/** 按格子里填的数改按钮上的字(认购配到约几股、发起招股募满你剩几成);打字时和每次刷新以后都调。 */
export const syncIpo = (root: ParentNode) => {
  root.querySelectorAll<HTMLInputElement>('input[data-ipo-amt]').forEach((el) => {
    const btn = el.parentElement?.querySelector<HTMLButtonElement>('[data-ipo-go]');
    if (!btn) return;
    const d = el.dataset;
    const t = ipoBuyLabel(Math.floor(Number(el.value) || 0), Number(d.price), Number(d.want), Number(d.pot), Number(d.shares));
    if (btn.textContent !== t) btn.textContent = t;
  });
  root.querySelectorAll<HTMLElement>('[data-ipo-form]').forEach((form) => {
    const btn = form.querySelector<HTMLButtonElement>('[data-ipo-go]');
    if (!btn) return;
    const f = ipoForm(form);
    const t = ipoLaunchLabel(f.want, f.price, Number(form.dataset.shares), Number(form.dataset.mine));
    if (btn.textContent !== t) btn.textContent = t;
  });
};
